import { CONNECTION_KEYS } from '@config/constants';
import { getItem, removeItem, setItem } from './localStorageService';
import type { ActiveConnectionState, SavedConnection } from '@/types/connection.types';

function parseConnections(raw: string | null): SavedConnection[] {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is SavedConnection => typeof item?.id === 'string');
  } catch {
    return [];
  }
}

function serializeConnections(connections: SavedConnection[]): void {
  setItem(CONNECTION_KEYS.savedConnections, JSON.stringify(connections));
}

export function listSavedConnections(): SavedConnection[] {
  return parseConnections(getItem(CONNECTION_KEYS.savedConnections));
}

export function getSavedConnection(id: string): SavedConnection | null {
  return listSavedConnections().find((connection) => connection.id === id) || null;
}

export function saveConnection(connection: SavedConnection): SavedConnection[] {
  const existing = listSavedConnections();
  const next = existing.some((item) => item.id === connection.id)
    ? existing.map((item) => (item.id === connection.id ? connection : item))
    : [...existing, connection];

  serializeConnections(next);
  return next;
}

export function deleteConnection(id: string): SavedConnection[] {
  const next = listSavedConnections().filter((connection) => connection.id !== id);
  serializeConnections(next);

  if (getActiveConnectionId() === id) {
    if (next[0]) {
      setActiveConnectionId(next[0].id);
    } else {
      clearActiveConnectionId();
    }
  }

  return next;
}

function activeConnectionKey(orgId?: string | null): string {
  const scopedOrgId = String(orgId || getActiveOrgId() || '').trim();
  return scopedOrgId
    ? `${CONNECTION_KEYS.activeConnectionId}:${scopedOrgId}`
    : CONNECTION_KEYS.activeConnectionId;
}

export function getActiveConnectionId(orgId?: string | null): string | null {
  const scoped = getItem(activeConnectionKey(orgId));
  if (scoped) return scoped;
  // One-time compatibility with the pre-org-scoped preference.
  return getItem(CONNECTION_KEYS.activeConnectionId);
}

export function setActiveConnectionId(id: string, orgId?: string | null): void {
  const key = activeConnectionKey(orgId);
  setItem(key, id);
  if (key !== CONNECTION_KEYS.activeConnectionId) {
    removeItem(CONNECTION_KEYS.activeConnectionId);
  }
}

export function clearActiveConnectionId(orgId?: string | null): void {
  const key = activeConnectionKey(orgId);
  removeItem(key);
  if (key !== CONNECTION_KEYS.activeConnectionId) {
    removeItem(CONNECTION_KEYS.activeConnectionId);
  }
}

export function getActiveConnectionState(): ActiveConnectionState {
  const activeConnectionId = getActiveConnectionId();
  const activeConnection = activeConnectionId ? getSavedConnection(activeConnectionId) : null;

  if (activeConnectionId && !activeConnection) {
    clearActiveConnectionId();
    return { activeConnectionId: null, activeConnection: null };
  }

  return { activeConnectionId, activeConnection };
}

export function getActiveConnection(): SavedConnection | null {
  return getActiveConnectionState().activeConnection;
}

export function getActiveOrgId(): string | null {
  return getItem('activeOrgId');
}

export function setActiveOrgId(id: string): void {
  setItem('activeOrgId', id);
}

export function clearActiveOrgId(): void {
  removeItem('activeOrgId');
}
